import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash, FloppyDisk, Microphone, Stop } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ─── Voice → text helper (session-32) ─────────────────────────────────────
// Reusable mic button — records via MediaRecorder, POSTs the blob to the
// backend Whisper endpoint, and calls onText(transcript) so callers can
// splice it into their state. Language ('hi' | 'en' | undefined for auto)
// is picked from the same language dropdown on the field.
const MicButton = ({ onText, language, testId }) => {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const chunksRef = useRef([]);
  const recRef = useRef(null);

  const start = async () => {
    if (recording || busy) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      chunksRef.current = [];
      const mr = new MediaRecorder(stream);
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        try { stream.getTracks().forEach(t => t.stop()); } catch {}
        setBusy(true);
        try {
          const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
          if (!blob.size) { setBusy(false); return; }
          const fd = new FormData();
          fd.append('audio', blob, 'speech.webm');
          if (language) fd.append('language', language);
          const res = await axios.post(`${API}/admin/transcribe-audio`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          const t = (res.data?.text || '').trim();
          if (t) { onText(t); toast.success('Transcribed'); }
          else toast.info('No speech detected');
        } catch (e) {
          toast.error(e?.response?.data?.detail || 'Transcription failed');
        } finally { setBusy(false); }
      };
      mr.start();
      recRef.current = mr;
      setRecording(true);
    } catch (e) {
      toast.error('Mic access denied');
    }
  };

  const stop = () => {
    try { recRef.current?.stop(); } catch {}
    setRecording(false);
  };

  return (
    <button
      type="button"
      onClick={recording ? stop : start}
      disabled={busy}
      title={recording ? 'Stop & transcribe' : 'Speak in Hindi/English'}
      className={`inline-flex items-center justify-center w-7 h-7 rounded ${
        recording ? 'bg-red-500 text-white animate-pulse' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
      } ${busy ? 'opacity-50 cursor-wait' : ''}`}
      data-testid={testId}
    >
      {recording ? <Stop size={14} weight="fill" /> : busy ? <span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin"></span> : <Microphone size={14} weight="fill" />}
    </button>
  );
};

const EVENT_LABELS = {
  coin_cut_client: 'Coin Cut → Client',
  coin_cut_group: 'Coin Cut → Group Extras',
  deposit_client: 'Deposit → Client',
  deposit_cancel_client: 'Deposit Cancel → Client',
  withdraw_cancel_client: 'Withdraw Cancel → Client',
  withdraw_done_client: 'Withdraw Done → Client',
  first_hi_deposit: 'First "Hi" — Deposit',
  first_hi_withdrawal: 'First "Hi" — Withdrawal',
  first_hi_support: 'First "Hi" — Support',
  first_hi_inquiry: 'First "Hi" — Inquiry',
  first_hi_new: 'First "Hi" — New Customer (no MB)',
};

const CHAT_TOGGLES = [
  { key: 'deposit', label: 'Deposit Chat Bot', color: '#3b82f6' },
  { key: 'withdrawal', label: 'Withdrawal Chat Bot', color: '#f59e0b' },
  { key: 'support', label: 'Support Chat Bot', color: '#7c3aed' },
  { key: 'inquiry', label: 'Inquiry Chat Bot', color: '#0ea5e9' },
  { key: 'group', label: 'Group Chat Bot', color: '#0d9488' },
  { key: 'coin_cut', label: 'Coin Cut Auto-Message', color: '#ef4444' },
  { key: 'withdraw_cancel', label: 'Withdraw Cancel Auto-Message', color: '#f43f5e' },
  { key: 'deposit_cancel', label: 'Deposit Cancel Auto-Message', color: '#dc2626' },
];

// MB tab list is now dynamic — pulled from /admin/mb-registry so newly-created
// MBs (via MB Management section) automatically appear here. 'N' is a virtual
// tab representing NEW customers with no MB assigned yet — its templates are
// stored under the `first_hi_new` key rather than an mb_overrides entry.
const STATIC_TABS_HEAD = ['all'];
const STATIC_TABS_TAIL = ['N'];

const emptyItem = () => ({ text: '', link: '', language: 'en', mode: 'single', options: [] });

const BotConfigEditor = ({ adminId }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeMb, setActiveMb] = useState('all');
  const [mbList, setMbList] = useState([]);  // dynamic MB names from registry

  useEffect(() => {
    axios.get(`${API}/admin/bot-config`)
      .then(res => setConfig(res.data))
      .catch(() => toast.error('Failed to load bot config'))
      .finally(() => setLoading(false));
    // Fetch MB registry so the tab row reflects newly-created / hidden MBs
    axios.get(`${API}/admin/mb-registry`, { params: { user_id: adminId } })
      .then(res => setMbList((res.data?.items || [])
        .filter(m => !m.hidden)
        .map(m => m.name)))
      .catch(() => { /* fallback to defaults handled below */ });
  }, [adminId]);

  const MB_TABS = [
    ...STATIC_TABS_HEAD,
    ...(mbList.length ? mbList : ['MB7', 'MB8', 'MB9', 'MB10', 'MB11']),
    ...STATIC_TABS_TAIL,
  ];

  const toggleChat = async (key) => {
    const next = { ...config.toggles, [key]: !config.toggles[key] };
    setConfig({ ...config, toggles: next });
    try {
      await axios.put(`${API}/admin/bot-config`, { admin_id: adminId, toggles: next });
      toast.success(`${key} bot ${next[key] ? 'activated' : 'deactivated'}`);
    } catch { toast.error('Toggle save failed'); }
  };

  // ─── Template helpers scoped to `activeMb` ────────────────────────────────
  const getTemplates = () => {
    if (activeMb === 'all') return config.templates || {};
    if (activeMb === 'N') {
      // 'N' is a virtual tab that maps to the global first_hi_new template.
      return { first_hi_new: (config.templates || {}).first_hi_new || [] };
    }
    return (config.mb_overrides && config.mb_overrides[activeMb]) || {};
  };

  const setTemplates = (next) => {
    if (activeMb === 'all') {
      setConfig({ ...config, templates: next });
    } else if (activeMb === 'N') {
      // Save first_hi_new back to the global template map.
      setConfig({
        ...config,
        templates: { ...(config.templates || {}), first_hi_new: next.first_hi_new || [] },
      });
    } else {
      setConfig({
        ...config,
        mb_overrides: { ...(config.mb_overrides || {}), [activeMb]: next },
      });
    }
  };

  const addMessage = (eventKey) => {
    const templates = getTemplates();
    const cur = templates[eventKey] || [];
    setTemplates({ ...templates, [eventKey]: [...cur, emptyItem()] });
  };

  const removeMessage = (eventKey, idx) => {
    const templates = getTemplates();
    const cur = [...(templates[eventKey] || [])];
    cur.splice(idx, 1);
    setTemplates({ ...templates, [eventKey]: cur });
  };

  const updateField = (eventKey, idx, field, value) => {
    const templates = getTemplates();
    const cur = [...(templates[eventKey] || [])];
    cur[idx] = { ...cur[idx], [field]: value };
    setTemplates({ ...templates, [eventKey]: cur });
  };

  const addOption = (eventKey, idx) => {
    const templates = getTemplates();
    const cur = [...(templates[eventKey] || [])];
    const opts = [...(cur[idx].options || [])];
    if (opts.length >= 6) { toast.info('Max 6 options'); return; }
    opts.push({ label: '', reply: '', link: '' });
    cur[idx] = { ...cur[idx], options: opts };
    setTemplates({ ...templates, [eventKey]: cur });
  };

  const removeOption = (eventKey, idx, optIdx) => {
    const templates = getTemplates();
    const cur = [...(templates[eventKey] || [])];
    const opts = [...(cur[idx].options || [])];
    opts.splice(optIdx, 1);
    cur[idx] = { ...cur[idx], options: opts };
    setTemplates({ ...templates, [eventKey]: cur });
  };

  const updateOption = (eventKey, idx, optIdx, field, value) => {
    const templates = getTemplates();
    const cur = [...(templates[eventKey] || [])];
    const opts = [...(cur[idx].options || [])];
    opts[optIdx] = { ...opts[optIdx], [field]: value };
    cur[idx] = { ...cur[idx], options: opts };
    setTemplates({ ...templates, [eventKey]: cur });
  };

  const saveTemplates = async () => {
    setSaving(true);
    try {
      const body = { admin_id: adminId };
      if (activeMb === 'all') {
        body.templates = config.templates;
      } else if (activeMb === 'N') {
        // Session-32: N tab lives inside global `templates.first_hi_new`.
        // Sending it as an mb_override would be rejected by the backend.
        body.templates = { first_hi_new: (config.templates || {}).first_hi_new || [] };
      } else {
        body.mb_overrides = { [activeMb]: config.mb_overrides?.[activeMb] || {} };
      }
      await axios.put(`${API}/admin/bot-config`, body);
      toast.success(
        activeMb === 'all' ? 'Global templates saved'
        : activeMb === 'N' ? 'New-customer welcome saved'
        : `${activeMb} overrides saved`
      );
    } catch (e) {
      toast.error('Save failed: ' + (e?.response?.data?.detail || e?.message));
    } finally { setSaving(false); }
  };

  const clearMbOverride = async () => {
    if (activeMb === 'all') return;
    if (!window.confirm(`Clear all ${activeMb} overrides and fall back to global templates?`)) return;
    setConfig({ ...config, mb_overrides: { ...(config.mb_overrides || {}), [activeMb]: {} } });
    try {
      await axios.put(`${API}/admin/bot-config`, { admin_id: adminId, mb_overrides: { [activeMb]: {} } });
      toast.success(`${activeMb} overrides cleared`);
    } catch { toast.error('Clear failed'); }
  };

  if (loading || !config) return <div className="p-6 text-center text-gray-500">Loading bot config…</div>;

  const templates = getTemplates();
  const isMb = activeMb !== 'all';

  return (
    <div className="space-y-6" data-testid="bot-config-editor">
      <div className="bg-white rounded-xl shadow-sm p-4">
        <h3 className="font-bold text-slate-900 text-base mb-3">Bot Active / Inactive</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {CHAT_TOGGLES.map(t => (
            <label key={t.key} className="flex items-center justify-between p-3 border rounded-lg cursor-pointer hover:bg-slate-50" data-testid={`bot-toggle-${t.key}`}>
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full" style={{ background: t.color }} />
                <span className="text-sm font-semibold">{t.label}</span>
              </div>
              <input type="checkbox" checked={!!config.toggles[t.key]} onChange={() => toggleChat(t.key)} className="w-5 h-5 accent-emerald-600" />
            </label>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-4">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <h3 className="font-bold text-slate-900 text-base">Auto-Message Templates</h3>
            <p className="text-xs text-slate-500 mt-0.5">Placeholders: <code>{'{amount}'}</code>, <code>{'{username}'}</code>, <code>{'{utr}'}</code>, <code>{'{bank}'}</code>, <code>{'{reference}'}</code></p>
          </div>
          <div className="flex items-center gap-2">
            {isMb && (
              <button onClick={clearMbOverride} className="text-xs px-2.5 py-1.5 rounded border border-red-300 text-red-700 hover:bg-red-50 font-semibold" data-testid={`bot-clear-mb-${activeMb}`}>Clear {activeMb}</button>
            )}
            <button onClick={saveTemplates} disabled={saving} className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm flex items-center gap-2 disabled:opacity-50" data-testid="bot-templates-save-btn">
              <FloppyDisk size={14} weight="bold" /> {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5 mb-4 border-b pb-3" data-testid="bot-mb-tabs">
          {MB_TABS.map(mb => (
            <button key={mb} onClick={() => setActiveMb(mb)}
              data-testid={`bot-mb-tab-${mb}`}
              title={mb === 'N' ? 'New customers with no MB assigned yet' : ''}
              className={`px-3 py-1 rounded-full text-[11px] font-semibold border uppercase ${activeMb === mb ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'}`}>
              {mb === 'all' ? 'All MB (global)' : mb === 'N' ? 'N (new)' : mb}
            </button>
          ))}
        </div>

        {isMb && Object.keys(templates).length === 0 && (
          <div className="mb-4 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded p-2">
            No overrides yet for <b>{activeMb}</b>. Add a message below to override the global template only for this MB.
          </div>
        )}

        {Object.entries(EVENT_LABELS)
          .filter(([key]) => {
            // 'N' tab is dedicated to the first_hi_new template — hide the
            // rest. Other tabs hide first_hi_new so it stays visible only in
            // 'N' where it belongs.
            if (activeMb === 'N') return key === 'first_hi_new';
            return key !== 'first_hi_new';
          })
          .map(([key, label]) => {
          let arr = templates[key] || [];
          // Session-32: 'N' tab is capped at a single template — hide the
          // '+ Add' button once one exists, and auto-seed an empty item on
          // first open so the admin can just start typing without hunting
          // for the Add button.
          const isSingleSlot = activeMb === 'N' && key === 'first_hi_new';
          if (isSingleSlot && arr.length === 0) {
            arr = [{ text: '', link: '', language: 'en', mode: 'single', options: [] }];
          }
          return (
            <div key={key} className="mb-5 pb-4 border-b last:border-b-0" data-testid={`bot-event-${key}`}>
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-slate-800 text-sm">{label}</h4>
                {!isSingleSlot && (
                  <button onClick={() => addMessage(key)} className="text-xs px-2.5 py-1 rounded bg-emerald-50 text-emerald-700 hover:bg-emerald-100 font-semibold flex items-center gap-1" data-testid={`bot-add-msg-${key}`}>
                    <Plus size={12} weight="bold" /> Add
                  </button>
                )}
              </div>
              {arr.length === 0 && <div className="text-xs text-gray-400 italic">{isMb ? '(global fallback in use)' : 'No messages configured'}</div>}
              {arr.map((m, idx) => (
                <div key={idx} className="mb-2 p-3 rounded-lg border border-slate-200 bg-slate-50/50 space-y-2">
                  <div className="relative">
                    <textarea value={m.text} onChange={(e) => updateField(key, idx, 'text', e.target.value)} placeholder="Message text (newlines + placeholders supported)" rows={2} className="w-full px-2 py-1.5 pr-16 border border-slate-200 rounded text-xs resize-none" data-testid={`bot-msg-text-${key}-${idx}`} />
                    {/* Per-field mic + save (session-32) — mic dictates in
                        the language selected on the same row; save-icon
                        commits the entire config so a single field change
                        doesn't require scrolling to the bottom Save button. */}
                    <div className="absolute right-1 top-1 flex items-center gap-1">
                      <MicButton
                        language={m.language}
                        onText={(t) => updateField(key, idx, 'text', (m.text || '').trim() ? `${m.text.trim()} ${t}` : t)}
                        testId={`bot-msg-mic-${key}-${idx}`}
                      />
                      <button
                        type="button"
                        onClick={saveTemplates}
                        disabled={saving}
                        title="Save this field"
                        className="inline-flex items-center justify-center w-7 h-7 rounded bg-emerald-500 hover:bg-emerald-600 disabled:bg-emerald-300 text-white"
                        data-testid={`bot-msg-save-${key}-${idx}`}
                      >
                        <FloppyDisk size={14} weight="fill" />
                      </button>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <input type="text" value={m.link || ''} onChange={(e) => updateField(key, idx, 'link', e.target.value)} placeholder="Optional link (https://…)" className="flex-1 px-2 py-1.5 border border-slate-200 rounded text-xs" data-testid={`bot-msg-link-${key}-${idx}`} />
                    <select value={m.language || 'en'} onChange={(e) => updateField(key, idx, 'language', e.target.value)} className="px-2 py-1.5 border border-slate-200 rounded text-xs bg-white" data-testid={`bot-msg-lang-${key}-${idx}`}>
                      <option value="en">English</option>
                      <option value="hi">Hindi</option>
                    </select>
                    <select value={m.mode || 'single'} onChange={(e) => updateField(key, idx, 'mode', e.target.value)} className="px-2 py-1.5 border border-slate-200 rounded text-xs bg-white" data-testid={`bot-msg-mode-${key}-${idx}`}>
                      <option value="single">Plain</option>
                      <option value="options">Options</option>
                    </select>
                    {!isSingleSlot && (
                      <button onClick={() => removeMessage(key, idx)} className="p-1.5 rounded text-red-500 hover:bg-red-50" data-testid={`bot-remove-msg-${key}-${idx}`}><Trash size={16} weight="bold" /></button>
                    )}
                  </div>

                  {(m.mode === 'options') && (
                    <div className="space-y-1.5 border-t border-dashed border-slate-300 pt-2" data-testid={`bot-options-${key}-${idx}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-[11px] font-bold uppercase text-slate-500">Clickable Options</span>
                        <button onClick={() => addOption(key, idx)} className="text-[11px] px-2 py-0.5 rounded bg-indigo-50 text-indigo-700 hover:bg-indigo-100 font-semibold" data-testid={`bot-add-opt-${key}-${idx}`}>+ Option</button>
                      </div>
                      {(m.options || []).length === 0 && <div className="text-[11px] text-slate-400 italic">Add up to 6 options — each option can carry a next-reply text and/or a link.</div>}
                      {(m.options || []).map((op, oi) => (
                        <div key={oi} className="grid grid-cols-12 gap-1 items-center" data-testid={`bot-opt-${key}-${idx}-${oi}`}>
                          <input value={op.label} onChange={(e) => updateOption(key, idx, oi, 'label', e.target.value)} placeholder={`Option ${oi + 1} label`} className="col-span-3 px-2 py-1 border border-slate-200 rounded text-[11px]" />
                          <input value={op.reply} onChange={(e) => updateOption(key, idx, oi, 'reply', e.target.value)} placeholder="Next reply on click" className="col-span-5 px-2 py-1 border border-slate-200 rounded text-[11px]" />
                          <input value={op.link} onChange={(e) => updateOption(key, idx, oi, 'link', e.target.value)} placeholder="or link" className="col-span-3 px-2 py-1 border border-slate-200 rounded text-[11px]" />
                          <button onClick={() => removeOption(key, idx, oi)} className="col-span-1 p-1 rounded text-red-500 hover:bg-red-50"><Trash size={12} weight="bold" /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BotConfigEditor;
